import { useNavigate, useParams } from "react-router-dom";
import { FormEvent, useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Container from "@mui/material/Container";
import TextField from "@mui/material/TextField";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { Button, ButtonBase, IconButton, InputAdornment } from "@mui/material";

import Back from "../utils/Back";
import LOGO from "../../semiauth.logo.png";
import {
  useVaultResendPwLink,
  useVaultResetPassword,
} from "../../hooks/vault/base";

const ResetPassword = () => {
  const token = useParams().token;
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const toggleShowPassword = () => setShowPassword(!showPassword);

  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");

  const { mutate: reset, isSuccess } = useVaultResetPassword();

  const resendPwLink = useVaultResendPwLink();

  const handleResendLink = () => resendPwLink.mutate(token || "");

  const handlePasswordReset = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // attempt password recovery
    reset({ token, password, passwordConfirmation });
  };

  useEffect(() => {
    if (isSuccess) {
      setPassword("");
      setPasswordConfirmation("");

      navigate("/welcome");
    }
  }, [isSuccess]);

  useEffect(() => {
    if (resendPwLink.isSuccess) navigate("/welcome");
  }, [resendPwLink.isSuccess]);

  return (
    <Container
      component="main"
      maxWidth="sm"
      sx={{
        display: "flex",
        textAlign: "center",
        alignItems: "center",
        marginBlock: "auto",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
        }}
      >
        <img src={LOGO} alt="[semiauth] logo" className="logo" />

        <Box component="form" onSubmit={handlePasswordReset} noValidate>
          <TextField
            margin="normal"
            required
            fullWidth
            id="password"
            name="password"
            label="New password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="toggle password visibility"
                    onClick={toggleShowPassword}
                    edge="end"
                  >
                    {showPassword ? (
                      <VisibilityOffOutlinedIcon />
                    ) : (
                      <VisibilityOutlinedIcon />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            id="password-confirmation"
            name="password-confirmation"
            label="New password confirm"
            type={showPassword ? "text" : "password"}
            value={passwordConfirmation}
            onChange={(e) => setPasswordConfirmation(e.target.value)}
            autoComplete="password-confirmation"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="toggle password visibility"
                    onClick={toggleShowPassword}
                    edge="end"
                  >
                    {showPassword ? (
                      <VisibilityOffOutlinedIcon />
                    ) : (
                      <VisibilityOutlinedIcon />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            fullWidth
            type="submit"
            variant="contained"
            disabled={!password || !passwordConfirmation}
            sx={{ my: 2 }}
          >
            Reset password
          </Button>
          <Grid container>
            <Grid item>
              <Back />
            </Grid>

            <Grid item xs textAlign="right">
              <ButtonBase onClick={handleResendLink}>Resend link</ButtonBase>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Container>
  );
};

export default ResetPassword;
